'use client'
import { FilterContext, getFilterId } from '../../../../providers/filter-provider'
import { FilterOperator, FilterValue } from '../../../../types/Filters'
import { useContext, useEffect, useState } from 'react'

const OPERATOR = FilterOperator.EQUALS

/**
 * Hook used by select filters. Registers the filter and handles updates to the filter and its selected value.
 */
export const useSelectFilter = (filterName: string, initialValue?: FilterValue) => {
    const { filters, registerFilter, updateFilterValue, getFilterValue, getFilterById } = useContext(FilterContext)
    const filterId = getFilterId(filterName, OPERATOR)
    const [selectedValue, setSelectedValue] = useState<FilterValue>(getFilterValue(filterId))

    useEffect(() => {
        registerFilter({
            id: filterId,
            key: filterName,
            operator: OPERATOR,
            value: getFilterValue(filterId) ?? initialValue,
        })
    }, [])

    /**
     * Update the filter value when
     *  - the selected value updates and
     *  - differs from the filter value
     **/
    const handleSelectedValueUpdate = () => {
        const filter = getFilterById(filterId)

        if (filter && filter.value !== selectedValue) {
            updateFilterValue({ filter: filter, newValue: selectedValue })
        }
    }

    /**
     * Update the selected value when
     *  - filter exists and
     *  - selectedValue differs from the filter value
     */
    const handleFilterUpdate = () => {
        const filter = getFilterById(filterId)
        if (filter) {
            const matchingFilter = filters.find((stateFilter) => stateFilter.id === filter.id)
            if (matchingFilter && selectedValue !== matchingFilter?.value) {
                setSelectedValue(matchingFilter.value)
            }
        }
    }

    useEffect(() => {
        handleFilterUpdate()
    }, [filters])

    useEffect(() => {
        handleSelectedValueUpdate()
    }, [selectedValue])

    const fieldName = `select-filter_${filterName}[${OPERATOR}]`

    return {
        fieldName,
        selectedValue,
        setSelectedValue,
    }
}
